import { useState, useRef, type FormEvent } from 'react';
import {
  User,
  Lock,
  Edit,
  Save,
  X,
  Phone,
  Mail,
  MapPin,
  AlertTriangle,
  Camera,
  Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAthleteAuth, type AthleteProfile } from '@/contexts/AthleteAuthContext';
import { api } from '@/hooks/useApi';

interface EditableFields {
  phone: string;
  email: string;
  address: string;
  emergencyContact: string;
  emergencyPhone: string;
}

export default function AthleteProfilePage() {
  const { athlete, token, refreshProfile } = useAthleteAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<EditableFields>({
    phone: athlete?.phone || '',
    email: athlete?.email || '',
    address: athlete?.address || '',
    emergencyContact: athlete?.emergencyContact || '',
    emergencyPhone: athlete?.emergencyPhone || '',
  });

  if (!athlete) return null;

  const readOnlyFields: { label: string; labelEn: string; value: string }[] = [
    { label: '姓名', labelEn: 'Name', value: athlete.name },
    { label: '性別', labelEn: 'Gender', value: athlete.gender },
    { label: '出生日期', labelEn: 'Date of Birth', value: athlete.dateOfBirth },
    { label: '族別', labelEn: 'Ethnicity', value: athlete.ethnicity },
    { label: '鄉區', labelEn: 'Township', value: athlete.township },
  ];

  const editableFieldDefs: {
    key: keyof EditableFields;
    label: string;
    labelEn: string;
    icon: typeof Phone;
    type: string;
    placeholder: string;
  }[] = [
    { key: 'phone', label: '電話', labelEn: 'Phone', icon: Phone, type: 'tel', placeholder: '09XX-XXX-XXX' },
    { key: 'email', label: '電子郵件', labelEn: 'Email', icon: Mail, type: 'email', placeholder: 'email@example.com' },
    { key: 'address', label: '地址', labelEn: 'Address', icon: MapPin, type: 'text', placeholder: '通訊地址' },
    { key: 'emergencyContact', label: '緊急聯絡人', labelEn: 'Emergency Contact', icon: AlertTriangle, type: 'text', placeholder: '緊急聯絡人姓名' },
    { key: 'emergencyPhone', label: '緊急聯絡電話', labelEn: 'Emergency Phone', icon: Phone, type: 'tel', placeholder: '09XX-XXX-XXX' },
  ];

  const handleStartEdit = () => {
    setForm({
      phone: athlete.phone || '',
      email: athlete.email || '',
      address: athlete.address || '',
      emergencyContact: athlete.emergencyContact || '',
      emergencyPhone: athlete.emergencyPhone || '',
    });
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/me/profile', form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('資料更新成功 / Profile updated successfully');
      await refreshProfile();
      setEditing(false);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: { message?: string } } }).response?.data?.message ??
            '更新失敗 / Update failed')
          : '更新失敗 / Update failed';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('請選擇圖片檔案 / Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('檔案大小不能超過5MB / File size must be under 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      await api.put('/me/profile', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('照片上傳成功 / Photo uploaded successfully');
      await refreshProfile();
    } catch {
      toast.error('照片上傳失敗 / Photo upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)]">個人資料</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Profile</p>
        </div>
        {!editing && (
          <button
            onClick={handleStartEdit}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <Edit className="w-4 h-4" />
            <span>編輯 / Edit</span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Photo Section */}
        <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] p-6 shadow-sm flex flex-col items-center">
          <div className="relative">
            <div className="w-32 h-32 rounded-full overflow-hidden bg-[var(--bg-secondary)] border-4 border-[var(--border)]">
              {athlete.photoUrl ? (
                <img
                  src={athlete.photoUrl}
                  alt={athlete.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="w-16 h-16 text-[var(--text-secondary)]" />
                </div>
              )}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="absolute bottom-0 right-0 w-10 h-10 rounded-full bg-[var(--primary)] text-white flex items-center justify-center shadow-md hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {uploading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Camera className="w-5 h-5" />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handlePhotoUpload(file);
              }}
            />
          </div>
          <h2 className="mt-4 text-xl font-bold text-[var(--text-primary)]">{athlete.name}</h2>
          {athlete.nameEn && (
            <p className="text-sm text-[var(--text-secondary)]">{athlete.nameEn}</p>
          )}
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            {athlete.township} &middot; {athlete.ethnicity}
          </p>
        </div>

        {/* Details Section */}
        <div className="lg:col-span-2 space-y-6">
          {/* Read-only Fields */}
          <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-4 h-4 text-[var(--text-secondary)]" />
              <h2 className="text-lg font-bold text-[var(--text-primary)]">基本資料</h2>
              <span className="text-xs text-[var(--text-secondary)]">/ Basic Information</span>
            </div>
            <p className="text-xs text-[var(--text-secondary)] mb-4 bg-[var(--bg-secondary)] rounded-lg px-3 py-2">
              由鄉區工作人員管理，如需修改請聯繫工作人員
              <br />
              <span className="text-xs">Managed by township staff. Contact staff to modify.</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {readOnlyFields.map((field) => (
                <div key={field.labelEn}>
                  <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                    {field.label}{' '}
                    <span className="opacity-60">/ {field.labelEn}</span>
                  </label>
                  <div className="px-3 py-2.5 rounded-lg bg-[var(--bg-secondary)] text-sm text-[var(--text-primary)] border border-[var(--border)] opacity-75">
                    {field.value || '--'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Editable Fields */}
          <form
            onSubmit={handleSave}
            className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] p-6 shadow-sm"
          >
            <div className="flex items-center gap-2 mb-4">
              <Edit className="w-4 h-4 text-[var(--primary)]" />
              <h2 className="text-lg font-bold text-[var(--text-primary)]">聯絡資料</h2>
              <span className="text-xs text-[var(--text-secondary)]">/ Contact Information</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {editableFieldDefs.map((field) => (
                <div key={field.key}>
                  <label
                    htmlFor={field.key}
                    className="block text-xs font-medium text-[var(--text-secondary)] mb-1"
                  >
                    {field.label}{' '}
                    <span className="opacity-60">/ {field.labelEn}</span>
                  </label>
                  {editing ? (
                    <div className="relative">
                      <field.icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" />
                      <input
                        id={field.key}
                        type={field.type}
                        value={form[field.key]}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, [field.key]: e.target.value }))
                        }
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[var(--border)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent transition-shadow text-sm"
                        placeholder={field.placeholder}
                        disabled={saving}
                      />
                    </div>
                  ) : (
                    <div className="px-3 py-2.5 rounded-lg bg-[var(--bg-secondary)] text-sm text-[var(--text-primary)] border border-[var(--border)]">
                      {(athlete as AthleteProfile)[field.key] || (
                        <span className="text-[var(--text-secondary)] italic">未填寫 / Not provided</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {editing && (
              <div className="flex items-center gap-3 mt-6">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[var(--primary)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span>儲存 / Save</span>
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-[var(--border)] text-[var(--text-secondary)] text-sm font-medium hover:bg-[var(--bg-secondary)] disabled:opacity-50 transition-colors"
                >
                  <X className="w-4 h-4" />
                  <span>取消 / Cancel</span>
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
